// Lib
import { compact, filter, flow, includes, map, take, union, values } from 'lodash/fp';

// Utils
import { sortByScoreDescending } from './quickSearchUtils';
import quickSearch from './quickSearch';

// Selectors
import {
    currentBoardContentSearchSelector,
    quickNotesContentSearchSelector,
} from '../../../workspace/header/workspaceToolsHeader/elementSearch/elementSearchPopupSelector';
import { getCurrentBoardId } from '../../../element/selectors/currentBoardSelector';
import { getSortedViewedBoardIds } from '../../../user/navigationHistory/navigationHistorySelector';

const performQuickSearch = async (state, props) => {
    const { query, results } = props;

    if (!query) return [];

    const currentBoardId = getCurrentBoardId(state, props);
    const viewedBoardIds = getSortedViewedBoardIds(state, props);

    const quickNotesResult = quickNotesContentSearchSelector(state, props);
    const currentBoardResult = currentBoardContentSearchSelector(state, props);

    const quickSearchResults = await quickSearch({ query, viewedBoardIds, currentBoardId });

    const searchResultsByBoardId = results.reduce((resultAcc, result) => {
        const boardId = result.get('boardId');

        // Don't add the current board results from the search
        if (boardId === currentBoardId) return resultAcc;

        resultAcc[boardId] = resultAcc[boardId] || { id: boardId, matchElementIds: [], score: 0 };
        resultAcc[boardId].matchElementIds.push(result.get('id'));
        resultAcc[boardId].score += result.get('score');
        return resultAcc;
    }, {});

    // If a 'quick search' result is returned from an actual search, use the actual search result in its
    // place (as it will contain the matching elements)
    const quickSearchResultsWithChildren = quickSearchResults.map((qsResult) => {
        const matchedSearchResult = searchResultsByBoardId[qsResult.id];

        if (!matchedSearchResult) return qsResult;

        return {
            ...matchedSearchResult,
            score: qsResult.score,
            matchElementIds: union(qsResult.matchElementIds, matchedSearchResult.matchElementIds),
        };
    });

    const quickSearchResultIds = map('id', quickSearchResults);

    const limit = (Math.floor(results.size / 100) || 1) * 10;
    const searchResults = flow(
        values,
        // Filter the quick search results from the actual search results
        filter((searchResult) => !includes(searchResult.id, quickSearchResultIds)),
        sortByScoreDescending,
        take(limit),
    )(searchResultsByBoardId);

    return sortByScoreDescending(
        compact([quickNotesResult, currentBoardResult, ...quickSearchResultsWithChildren, ...searchResults]),
    );
};

export default performQuickSearch;
